import { useState, useEffect } from 'react';
import { PhotoIcon, XMarkIcon, ArrowUpTrayIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import type { Product, ProductFormData, ProductMetadata } from '../../types/product';
import { api } from '../../services/api';

interface ProductFormContentProps {
  initialData?: Product | null;
  onSubmit: (formData: ProductFormData) => Promise<void>;
  isSubmitting?: boolean;
}

export function ProductFormContent({ initialData, onSubmit, isSubmitting }: ProductFormContentProps) {
  const defaultMetadata: ProductMetadata = {
    material: '',
    variants: [],
    availableSizes: [],
    availableColors: {
      mens: [],
      womens: []
    },
    availableFits: ['mens']
  };

  // Helper function to ensure metadata is always properly initialized
  const ensureMetadata = (data: any) => {
    return data?.metadata 
      ? {
          material: data.metadata.material ?? defaultMetadata.material,
          variants: data.metadata.variants ?? defaultMetadata.variants,
          availableColors: {
            mens: data.metadata.availableColors?.mens ?? defaultMetadata.availableColors.mens,
            womens: data.metadata.availableColors?.womens ?? defaultMetadata.availableColors.womens
          },
          availableSizes: data.metadata.availableSizes ?? defaultMetadata.availableSizes,
          availableFits: data.metadata.availableFits ?? defaultMetadata.availableFits
        }
      : defaultMetadata;
  };

  const [formData, setFormData] = useState<ProductFormData>({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    price: initialData?.price ?? 0,
    imageUrl: initialData?.imageUrl ?? '',
    category: initialData?.category ?? '',
    type: initialData?.type ?? 'clothing',
    stockQuantity: initialData?.stockQuantity ?? 0,
    metadata: ensureMetadata(initialData),
    createdAt: initialData?.createdAt ?? new Date().toISOString(),
    updatedAt: initialData?.updatedAt ?? new Date().toISOString(),
    isActive: initialData?.isActive ?? true
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description,
        price: initialData.price,
        imageUrl: initialData.imageUrl || '',
        category: initialData.category,
        type: initialData.type,
        stockQuantity: initialData.stockQuantity,
        metadata: ensureMetadata(initialData),
        createdAt: initialData.createdAt,
        updatedAt: initialData.updatedAt,
        isActive: initialData.isActive
      });
      if (initialData.imageUrl) {
        setPreviewUrl(initialData.imageUrl);
      }
    }
  }, [initialData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setUploadError('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setUploadError('File must be an image');
        return;
      }
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imageFile) {
      try {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        const { imageUrl } = await api.admin.uploadProductImage(imageFormData);
        await onSubmit({ ...formData, imageUrl });
      } catch (error) {
        setUploadError('Failed to upload image');
        console.error('Error uploading image:', error);
        return;
      }
    } else {
      await onSubmit(formData);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSizesChange = (size: string) => {
    setFormData(prev => {
      const metadata = ensureMetadata(prev);
      return {
        ...prev,
        metadata: {
          ...metadata,
          availableSizes: metadata.availableSizes.includes(size)
            ? metadata.availableSizes.filter((s: string) => s !== size)
            : [...metadata.availableSizes, size]
        }
      };
    });
  };

  const handleColorsChange = (color: string, gender: 'mens' | 'womens') => {
    setFormData(prev => {
      const metadata = ensureMetadata(prev);
      return {
        ...prev,
        metadata: {
          ...metadata,
          availableColors: {
            ...metadata.availableColors,
            [gender]: metadata.availableColors[gender].includes(color)
              ? metadata.availableColors[gender].filter((c: string) => c !== color)
              : [...metadata.availableColors[gender], color]
          }
        }
      };
    });
  };

  const handleFitsChange = (fit: 'mens' | 'womens') => {
    setFormData(prev => {
      const metadata = ensureMetadata(prev);
      return {
        ...prev,
        metadata: {
          ...metadata,
          availableFits: metadata.availableFits.includes(fit)
            ? metadata.availableFits.filter((f: 'mens' | 'womens') => f !== fit)
            : [...metadata.availableFits, fit]
        }
      };
    });
  };

  const handleMetadataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const metadata = ensureMetadata(prev);
      return {
        ...prev,
        metadata: {
          ...metadata,
          [name]: value
        }
      };
    });
  };

  // Generate all possible variants from selected options
  const generateVariants = () => {
    setFormData(prev => {
      const metadata = ensureMetadata(prev);
      const { availableSizes, availableColors, availableFits } = metadata;
      
      // Create all possible combinations of size, color, and fit
      const newVariants = [];
      
      for (const fit of availableFits) {
        const colorsForFit = availableColors[fit as keyof typeof availableColors] || [];
        
        for (const size of availableSizes) {
          for (const color of colorsForFit) {
            // Create a unique ID for this variant
            const id = `${prev.name}-${size}-${color}-${fit}`.toLowerCase().replace(/\s+/g, '-');
            
            // Check if this variant already exists
            const existingVariant = metadata.variants.find((v: any) => 
              v.size === size && v.color === color && v.fit === fit
            );
            
            if (!existingVariant) {
              newVariants.push({
                id,
                size,
                color,
                fit,
                price: prev.price,
                stockQuantity: 10 // Default stock quantity
              });
            }
          }
        }
      }
      
      return {
        ...prev,
        metadata: {
          ...metadata,
          variants: [...metadata.variants, ...newVariants]
        }
      };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div>
        <label className="block text-sm font-medium text-gray-200 mb-2">
          Product Image
        </label>
        <div className="flex items-start space-x-6">
          <div className="relative h-40 w-40 flex-shrink-0 rounded-lg overflow-hidden bg-gray-800/50 border border-teal-500/20">
            {previewUrl ? (
              <>
                <img
                  src={previewUrl}
                  alt="Product preview"
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewUrl('');
                    setImageFile(null);
                  }}
                  className="absolute top-2 right-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center">
                <PhotoIcon className="h-12 w-12 text-gray-500" />
                <p className="mt-2 text-sm text-gray-400">No image selected</p>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start space-y-3">
            <label htmlFor="product-image-upload" className="relative inline-flex items-center px-4 py-2 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 border border-teal-500/20 cursor-pointer transition-colors">
              <ArrowUpTrayIcon className="mr-2 h-5 w-5 text-teal-500" />
              <span className="text-sm font-medium text-teal-500">Upload Image</span>
              <input
                type="file"
                id="product-image-upload"
                name="image"
                accept="image/*"
                onChange={handleImageChange}
                className="sr-only"
              />
            </label>
            <p className="text-sm text-gray-400">
              Supported formats: JPG, PNG, GIF (max 5MB)
            </p>
            {uploadError && (
              <p className="text-sm text-red-400 flex items-center">
                <ExclamationCircleIcon className="mr-1 h-4 w-4" />
                {uploadError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
            Product Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 px-4 py-2.5 text-gray-100 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            required
          />
        </div>

        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-200 mb-2">
            Price
          </label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <span className="text-gray-400">$</span>
            </div>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 pl-8 pr-4 py-2.5 text-gray-100 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              min="0"
              step="0.01"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-200 mb-2">
            Category
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 px-4 py-2.5 text-gray-100 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            required
          >
            <option value="">Select a category</option>
            <option value="men">Men</option>
            <option value="women">Women</option>
            <option value="accessories">Accessories</option>
          </select>
        </div>

        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-200 mb-2">
            Product Type
          </label>
          <div className="mt-1">
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="block w-full rounded-md bg-gray-800/50 border border-teal-500/20 py-2 px-3 text-gray-100 focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            >
              <option value="clothing">Clothing</option>
              <option value="accessory">Accessory</option>
              <option value="collectible">Collectible</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-200 mb-2">
            Stock Quantity
          </label>
          <input
            type="number"
            id="stockQuantity"
            name="stockQuantity"
            value={formData.stockQuantity}
            onChange={handleInputChange}
            className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 px-4 py-2.5 text-gray-100 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            min="0"
            required
          />
        </div>

        <div>
          <label htmlFor="material" className="block text-sm font-medium text-gray-200 mb-2">
            Material
          </label>
          <input
            type="text"
            id="material"
            name="material"
            value={formData.metadata?.material || ''}
            onChange={handleMetadataChange}
            className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 px-4 py-2.5 text-gray-100 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            required
          />
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-200 mb-2">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={4}
          className="w-full rounded-lg bg-gray-800/50 border border-teal-500/20 px-4 py-2.5 text-gray-100 placeholder-gray-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          required
        />
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-200 mb-3">
            Available Sizes
          </label>
          <div className="flex flex-wrap gap-2">
            {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => handleSizesChange(size)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.metadata?.availableSizes?.includes(size)
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-3">
            Men's Colors
          </label>
          <div className="flex flex-wrap gap-2">
            {['Black', 'White', 'Gray', 'Navy', 'Red', 'Green'].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorsChange(color, 'mens')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.metadata?.availableColors?.mens?.includes(color)
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                }`}
              >
                {color}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-3">
            Women's Colors
          </label>
          <div className="flex flex-wrap gap-2">
            {['Black', 'White', 'Gray', 'Navy', 'Red', 'Green'].map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorsChange(color, 'womens')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.metadata?.availableColors?.womens?.includes(color)
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                }`}
              >
                {color}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-200 mb-3">
            Available Fits
          </label>
          <div className="flex flex-wrap gap-2">
            {(['mens', 'womens'] as const).map((fit) => (
              <button
                key={fit}
                type="button"
                onClick={() => handleFitsChange(fit)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.metadata?.availableFits?.includes(fit)
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                }`}
              >
                {fit === 'mens' ? "Men's" : "Women's"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Variants Section (Only for clothing) */}
      {formData.type === 'clothing' && (
        <div className="border border-teal-500/20 rounded-lg p-4 bg-gray-800/20">
          <h3 className="text-lg font-medium text-gray-100 mb-4">Product Variants</h3>
          
          {/* Material */}
          <div className="mb-4">
            <label htmlFor="material" className="block text-sm font-medium text-gray-200 mb-2">
              Material
            </label>
            <input
              type="text"
              id="material"
              name="material"
              value={formData.metadata?.material || ''}
              onChange={handleMetadataChange}
              className="block w-full rounded-md bg-gray-800/50 border border-teal-500/20 py-2 px-3 text-gray-100 focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
            />
          </div>
          
          {/* Available Sizes */}
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-200 mb-2">Available Sizes</span>
            <div className="flex flex-wrap gap-2">
              {['XS', 'S', 'M', 'L', 'XL', '2XL'].map(size => (
                <button
                  key={size}
                  type="button"
                  onClick={() => handleSizesChange(size)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    formData.metadata?.availableSizes?.includes(size)
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
          
          {/* Available Fits */}
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-200 mb-2">Available Fits</span>
            <div className="flex flex-wrap gap-2">
              {['mens', 'womens'].map(fit => (
                <button
                  key={fit}
                  type="button"
                  onClick={() => handleFitsChange(fit as 'mens' | 'womens')}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    formData.metadata?.availableFits?.includes(fit as 'mens' | 'womens')
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                  }`}
                >
                  {fit === 'mens' ? "Men's" : "Women's"}
                </button>
              ))}
            </div>
          </div>
          
          {/* Available Colors (Men's) */}
          {formData.metadata?.availableFits?.includes('mens') && (
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-200 mb-2">Available Colors (Men's)</span>
              <div className="flex flex-wrap gap-2">
                {['Black', 'White', 'Navy', 'Gray', 'Green', 'Red'].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorsChange(color, 'mens')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      formData.metadata?.availableColors?.mens?.includes(color)
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Available Colors (Women's) */}
          {formData.metadata?.availableFits?.includes('womens') && (
            <div className="mb-4">
              <span className="block text-sm font-medium text-gray-200 mb-2">Available Colors (Women's)</span>
              <div className="flex flex-wrap gap-2">
                {['Black', 'White', 'Navy', 'Gray', 'Pink', 'Purple'].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorsChange(color, 'womens')}
                    className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      formData.metadata?.availableColors?.womens?.includes(color)
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-800/50 text-gray-300 hover:bg-gray-800 border border-teal-500/20'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Generate Variants Button */}
          <div className="mt-6">
            <button
              type="button"
              onClick={generateVariants}
              className="px-4 py-2 bg-teal-600 text-white font-medium rounded-md hover:bg-teal-700 transition-colors"
            >
              Generate All Variants
            </button>
            <p className="mt-2 text-sm text-gray-400">
              This will create all possible combinations of sizes, colors, and fits with a default stock of 10 each.
            </p>
          </div>
          
          {/* Variant List */}
          {formData.type === 'clothing' && formData.metadata && formData.metadata.variants && formData.metadata.variants.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-200 mb-2">
                Created Variants ({formData.metadata.variants.length})
              </h4>
              <div className="max-h-60 overflow-y-auto mt-2 bg-gray-800/50 border border-teal-500/20 rounded-md">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-700/30">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">Size</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">Color</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">Fit</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-300">Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/30">
                    {formData.metadata.variants.map((variant, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 text-xs text-gray-300">{variant.size}</td>
                        <td className="px-3 py-2 text-xs text-gray-300">{variant.color}</td>
                        <td className="px-3 py-2 text-xs text-gray-300">{variant.fit}</td>
                        <td className="px-3 py-2 text-xs text-gray-300">{variant.stockQuantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center px-6 py-3 rounded-lg bg-teal-500 text-white font-medium hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting && (
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {initialData ? 'Update Product' : 'Add Product'}
        </button>
      </div>
    </form>
  );
} 